/**
 * Design tokens of the portal, taken from the Bobbee product mockups (teal sidebar, white
 * surfaces, Tailwind-like teal/gray scale). Change the brand here only: MUI themes and
 * Backstage UI CSS (bui.css) derive from it.
 */

/** Bobbee brand color (teal of the Bobbee app). */
export const BRAND = {
  primary: '#009689',
  primaryHover: '#00786F',
  /** Brand on dark surfaces (lighter for contrast). */
  primaryDark: '#00D5BE',
  primaryDarkHover: '#46ECD5',
  /** Bee yellow of the Bobbee logo, the secondary color of the portal. */
  accent: '#FFB940',
  accentHover: '#F5A524',
};

export type Mode = 'light' | 'dark';

export interface ModeTokens {
  app: string;
  surface: string;
  surfaceSubtle: string;
  border: string;
  borderStrong: string;
  text: string;
  textSecondary: string;
  textMuted: string;
  brand: string;
  brandHover: string;
  brandSoft: string;
  onBrand: string;
  accent: string;
  accentHover: string;
  accentSoft: string;
  onAccent: string;
  success: string;
  successSoft: string;
  warning: string;
  warningSoft: string;
  error: string;
  errorSoft: string;
  info: string;
  infoSoft: string;
  purple: string;
  purpleSoft: string;
  neutral: string;
  neutralSoft: string;
  /** Sidebar: vertical gradient, selected item and text colors. */
  sidebarTop: string;
  sidebarBottom: string;
  sidebarSelected: string;
  sidebarHover: string;
  sidebarText: string;
  sidebarTextMuted: string;
  sidebarDivider: string;
}

export const TOKENS: Record<Mode, ModeTokens> = {
  light: {
    app: '#F9FAFB',
    surface: '#FFFFFF',
    surfaceSubtle: '#F9FAFB',
    border: '#E5E7EB',
    borderStrong: '#D1D5DC',
    text: '#101828',
    textSecondary: '#4A5565',
    textMuted: '#6A7282',
    brand: BRAND.primary,
    brandHover: BRAND.primaryHover,
    brandSoft: '#F0FDFA',
    onBrand: '#FFFFFF',
    accent: BRAND.accent,
    accentHover: BRAND.accentHover,
    accentSoft: '#FFF4DC',
    onAccent: '#101828',
    success: '#008236',
    successSoft: '#F0FDF4',
    warning: '#BB4D00',
    warningSoft: '#FFFBEB',
    error: '#C10007',
    errorSoft: '#FEF2F2',
    info: '#1447E6',
    infoSoft: '#EFF6FF',
    purple: '#8200DB',
    purpleSoft: '#FAF5FF',
    neutral: '#4A5565',
    neutralSoft: '#F3F4F6',
    sidebarTop: '#009689',
    sidebarBottom: '#00786F',
    sidebarSelected: '#005F5A',
    sidebarHover: 'rgba(255, 255, 255, 0.10)',
    sidebarText: '#FFFFFF',
    sidebarTextMuted: 'rgba(255, 255, 255, 0.85)',
    sidebarDivider: 'rgba(255, 255, 255, 0.18)',
  },
  dark: {
    app: '#0B0F14',
    surface: '#131820',
    surfaceSubtle: '#181E27',
    border: '#252C37',
    borderStrong: '#343C49',
    text: '#F3F4F6',
    textSecondary: '#99A1AF',
    textMuted: '#7E8796',
    brand: BRAND.primaryDark,
    brandHover: BRAND.primaryDarkHover,
    brandSoft: 'rgba(0, 213, 190, 0.12)',
    onBrand: '#0B0F14',
    accent: BRAND.accent,
    accentHover: BRAND.accentHover,
    accentSoft: 'rgba(255, 185, 64, 0.22)',
    onAccent: '#101828',
    success: '#05DF72',
    successSoft: 'rgba(5, 223, 114, 0.12)',
    warning: '#FFB900',
    warningSoft: 'rgba(255, 185, 0, 0.12)',
    error: '#FF6467',
    errorSoft: 'rgba(255, 100, 103, 0.12)',
    info: '#51A2FF',
    infoSoft: 'rgba(81, 162, 255, 0.12)',
    purple: '#C27AFF',
    purpleSoft: 'rgba(194, 122, 255, 0.12)',
    neutral: '#99A1AF',
    neutralSoft: 'rgba(153, 161, 175, 0.12)',
    sidebarTop: '#00544F',
    sidebarBottom: '#023A37',
    sidebarSelected: '#00786F',
    sidebarHover: 'rgba(255, 255, 255, 0.08)',
    sidebarText: '#FFFFFF',
    sidebarTextMuted: 'rgba(255, 255, 255, 0.80)',
    sidebarDivider: 'rgba(255, 255, 255, 0.14)',
  },
};

export const RADIUS = 8;

export const FONT_FAMILY =
  '"Inter Variable", Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
